import type {
  CreateLogbookCaseRequest, LogbookAdminStatus, LogbookAggregationQuery, LogbookAggregationResult,
  LogbookAttachResult, LogbookCapabilities, LogbookCase, LogbookCaseDetail, LogbookCaseFilters,
  LogbookCaseItemType, LogbookCaseStatus, LogbookCaseSubjectRole, LogbookCasesResult, LogbookEvent,
  LogbookJobKind, LogbookJobResult, LogbookJobSubmitted, LogbookQuery, LogbookRollupQuery,
  LogbookRollupResult, LogbookSearchResult, LogbookServersResult, LogbookSyncResult,
} from '../types/logbook';

/**
 * Logbook evidence service.
 *
 * Every call goes to the Argus worker, which holds the Loglibrarian credential and decides
 * authorization. The browser never sees a Logbook token and never reaches the private service.
 *
 * Filters travel in a JSON body rather than a query string, because that is what Loglibrarian
 * accepts. Errors carry a machine code and a sentence the UI can show as written: the worker has
 * already translated the upstream status, and the upstream message is never forwarded.
 */

const BASE = import.meta.env.DEV ? 'https://spy.playcdu.co/api/notes/logbook' : '/api/notes/logbook';

export class LogbookApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
    this.name = 'LogbookApiError';
  }

  /** A cursor or a job handle that expired is worth retrying from a fresh search, not an error to dwell on. */
  get isExpired(): boolean {
    return this.code === 'cursor_expired' || this.status === 410;
  }

  get isNotConfigured(): boolean {
    return this.code === 'not_configured';
  }

  /** The query was too broad for Logbook's query budget, which the UI can suggest fixing. */
  get isTooBroad(): boolean {
    return this.code === 'query_budget' || this.code === 'upstream_too_large';
  }
}

const FALLBACK: Record<number, string> = {
  401: 'Your Argus session has expired. Sign in again.',
  403: 'Your account does not have that Logbook permission.',
  404: 'That evidence is not in the retained window, or it is outside the authorized servers.',
  429: 'Logbook is busy. Please retry in a few seconds.',
  502: 'Logbook could not be reached. Please retry.',
  503: 'Logbook is unavailable. Please retry shortly.',
  504: 'Logbook took too long to answer. Narrow the search and retry.',
};

/** Above the backend's own 45 second upstream deadline, so a slow search reports the upstream answer rather than a local timeout. */
const REQUEST_TIMEOUT_MS = 60_000;

async function readError(response: Response): Promise<LogbookApiError> {
  const fallback = FALLBACK[response.status] ?? 'Logbook could not complete that request. Please retry.';
  if (!response.headers.get('content-type')?.includes('application/json')) {
    return new LogbookApiError(response.status, 'unavailable', fallback);
  }
  try {
    const data = await response.json() as { error?: string; message?: string };
    return new LogbookApiError(response.status, data.error || 'unavailable', data.message || fallback);
  } catch {
    return new LogbookApiError(response.status, 'unavailable', fallback);
  }
}

async function request<T>(path: string, init: RequestInit & { signal?: AbortSignal } = {}): Promise<T> {
  const controller = new AbortController();
  const abort = () => controller.abort();
  init.signal?.addEventListener('abort', abort, { once: true });
  if (init.signal?.aborted) controller.abort();
  let timedOut = false;
  const timer = setTimeout(() => { timedOut = true; controller.abort(); }, REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(`${BASE}${path}`, {
      ...init,
      credentials: 'same-origin',
      cache: 'no-store',
      signal: controller.signal,
      headers: {
        Accept: 'application/json',
        ...(init.body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...init.headers,
      },
    });
    if (!response.ok) throw await readError(response);
    // A redirect to the Access login page is an expired session, not evidence.
    if (response.redirected || !response.headers.get('content-type')?.includes('application/json')) {
      throw new LogbookApiError(401, 'unauthorized', FALLBACK[401]);
    }
    return await response.json() as T;
  } catch (error) {
    if (timedOut) throw new LogbookApiError(504, 'timeout', FALLBACK[504]);
    if (error instanceof LogbookApiError) throw error;
    if (controller.signal.aborted) throw error;
    throw new LogbookApiError(502, 'unavailable', FALLBACK[502]);
  } finally {
    clearTimeout(timer);
    init.signal?.removeEventListener('abort', abort);
  }
}

/** Drops empty filters so the strict upstream validator never sees a blank string. */
function clean<T extends object>(query: T): T {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value) && value.length === 0) continue;
    out[key] = value;
  }
  return out as T;
}

const post = <T>(path: string, body: object, signal?: AbortSignal) =>
  request<T>(path, { method: 'POST', body: JSON.stringify(clean(body)), signal });

function search(params: Record<string, string | number | undefined>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') query.set(key, String(value));
  }
  const rendered = query.toString();
  return rendered ? `?${rendered}` : '';
}

class LogbookApiService {
  // ── Discovery ─────────────────────────────────────────────────────────────

  getCapabilities(signal?: AbortSignal): Promise<LogbookCapabilities> {
    return request('/capabilities', { signal });
  }

  getServers(signal?: AbortSignal): Promise<LogbookServersResult> {
    return request('/servers', { signal });
  }

  getServerHistory(signal?: AbortSignal): Promise<{ servers?: unknown[]; [key: string]: unknown }> {
    return request('/servers/history', { signal });
  }

  /** Last tracked insertion receipt per server: how fresh the central copy is. */
  getSync(signal?: AbortSignal): Promise<LogbookSyncResult> {
    return request('/sync', { signal });
  }

  // ── Evidence ──────────────────────────────────────────────────────────────

  search(query: LogbookQuery, signal?: AbortSignal): Promise<LogbookSearchResult> {
    return post('/search', query, signal);
  }

  /** The same query contract as search, but an actor is required. */
  timeline(query: LogbookQuery & ({ actor_id: string } | { actor_name: string }), signal?: AbortSignal): Promise<LogbookSearchResult> {
    return post('/timeline', query, signal);
  }

  /** Counts every matching event in the window, not just the page on screen. */
  aggregate(query: LogbookAggregationQuery, signal?: AbortSignal): Promise<LogbookAggregationResult> {
    return post('/aggregations', query, signal);
  }

  getEvent(eventId: string, signal?: AbortSignal): Promise<LogbookEvent> {
    return request(`/events/${encodeURIComponent(eventId)}`, { signal });
  }

  /** Hourly position summaries, separate from raw evidence and only where rollups are enabled. */
  searchRollups(query: LogbookRollupQuery, signal?: AbortSignal): Promise<LogbookRollupResult> {
    return post('/rollups/search', query, signal);
  }

  // ── Jobs and export ───────────────────────────────────────────────────────

  submitJob(kind: LogbookJobKind, query: LogbookQuery, signal?: AbortSignal): Promise<LogbookJobSubmitted> {
    return request('/jobs', { method: 'POST', body: JSON.stringify({ kind, query: clean(query) }), signal });
  }

  getJob(jobId: string, signal?: AbortSignal): Promise<LogbookJobResult> {
    return request(`/jobs/${encodeURIComponent(jobId)}`, { signal });
  }

  cancelJob(jobId: string, signal?: AbortSignal): Promise<{ state: string }> {
    return request(`/jobs/${encodeURIComponent(jobId)}`, { method: 'DELETE', signal });
  }

  /**
   * Polls one job to completion. Jobs are not persistent: a restart or the fifteen minute handle
   * lifetime ends them, and repeated copies of the same query are what the API asks callers not to
   * create, so this waits on one handle rather than resubmitting.
   */
  async awaitJob(jobId: string, options: { signal?: AbortSignal; intervalMs?: number; timeoutMs?: number } = {}): Promise<LogbookJobResult> {
    const interval = options.intervalMs ?? 1200;
    const deadline = Date.now() + (options.timeoutMs ?? 120_000);
    for (;;) {
      const job = await this.getJob(jobId, options.signal);
      if (job.state !== 'pending') return job;
      if (Date.now() > deadline) throw new LogbookApiError(504, 'timeout', 'That job is still running. Narrow the filters and try again.');
      await new Promise<void>((resolve, reject) => {
        const timer = setTimeout(resolve, interval);
        options.signal?.addEventListener('abort', () => { clearTimeout(timer); reject(new DOMException('Aborted', 'AbortError')); }, { once: true });
      });
    }
  }

  // ── Cases ─────────────────────────────────────────────────────────────────
  //
  // A case is Argus's own and reaches Loglibrarian on no path. The record travels with the request
  // from the page that was displaying it, so a case can be built and read while the evidence service
  // is unavailable, and it still reads after retention or a rollup takes the original away.

  listCases(filters: LogbookCaseFilters = {}, signal?: AbortSignal): Promise<LogbookCasesResult> {
    return request(`/cases${search(filters as Record<string, string | number | undefined>)}`, { signal });
  }

  /** Accepts a case id or its reference, so a link can quote either. */
  getCase(caseId: string, signal?: AbortSignal): Promise<LogbookCaseDetail> {
    return request(`/cases/${encodeURIComponent(caseId)}`, { signal });
  }

  /** Every case attached to one note, warning or ban. */
  casesForItem(itemType: LogbookCaseItemType, itemId: string, signal?: AbortSignal): Promise<{ cases: LogbookCase[] }> {
    return request(`/cases/for/${encodeURIComponent(itemType)}/${encodeURIComponent(itemId)}`, { signal });
  }

  createCase(body: CreateLogbookCaseRequest, signal?: AbortSignal): Promise<LogbookCase & { attach: LogbookAttachResult | null }> {
    return post('/cases', body, signal);
  }

  updateCase(
    caseId: string,
    changes: { title?: string; summary?: string; status?: LogbookCaseStatus },
    signal?: AbortSignal,
  ): Promise<LogbookCaseDetail> {
    return request(`/cases/${encodeURIComponent(caseId)}`, { method: 'PATCH', body: JSON.stringify(clean(changes)), signal });
  }

  /** Destroys the only permanent copy of the case's evidence, so it needs the admin node. */
  deleteCase(caseId: string, signal?: AbortSignal): Promise<{ deleted: string; reference: string }> {
    return request(`/cases/${encodeURIComponent(caseId)}`, { method: 'DELETE', signal });
  }

  /** Sends the records themselves: nothing is re-read from Logbook to build the copy. */
  attachRecords(caseId: string, records: LogbookEvent[], note?: string, signal?: AbortSignal): Promise<LogbookAttachResult> {
    return post(`/cases/${encodeURIComponent(caseId)}/records`, { records, note }, signal);
  }

  detachRecord(caseId: string, recordId: string, signal?: AbortSignal): Promise<{ detached: string }> {
    return request(`/cases/${encodeURIComponent(caseId)}/records/${encodeURIComponent(recordId)}`, { method: 'DELETE', signal });
  }

  linkCase(caseId: string, itemType: LogbookCaseItemType, itemId: string, signal?: AbortSignal): Promise<LogbookCaseDetail> {
    return post(`/cases/${encodeURIComponent(caseId)}/links`, { item_type: itemType, item_id: itemId }, signal);
  }

  unlinkCase(caseId: string, linkId: string, signal?: AbortSignal): Promise<{ unlinked: string }> {
    return request(`/cases/${encodeURIComponent(caseId)}/links/${encodeURIComponent(linkId)}`, { method: 'DELETE', signal });
  }

  addSubject(caseId: string, uuid: string, role: LogbookCaseSubjectRole, note?: string, signal?: AbortSignal): Promise<LogbookCaseDetail> {
    return post(`/cases/${encodeURIComponent(caseId)}/subjects`, { uuid, role, note }, signal);
  }

  removeSubject(caseId: string, subjectId: string, signal?: AbortSignal): Promise<{ removed: string }> {
    return request(`/cases/${encodeURIComponent(caseId)}/subjects/${encodeURIComponent(subjectId)}`, { method: 'DELETE', signal });
  }

  // ── Service administration (read only) ────────────────────────────────────

  getAdminStatus(signal?: AbortSignal): Promise<LogbookAdminStatus> {
    return request('/admin/status', { signal });
  }

  getRetention(signal?: AbortSignal): Promise<{ retention_days: number }> {
    return request('/admin/retention', { signal });
  }

  getRollupSettings(signal?: AbortSignal): Promise<{ enabled: boolean; telemetry_raw_days: number }> {
    return request('/admin/rollups', { signal });
  }
}

export const logbookApiService = new LogbookApiService();
